/**
 * Dashboard — KPIs operativos + accesos rápidos + operaciones recientes.
 *
 * Reemplaza el placeholder de Fase 2. Los KPIs se calculan en runtime sobre
 * el GLN activo (no hay precomputo en el store — el patrón derivado vale para
 * todo lo que dependa del estado SNT).
 *
 *   - Remitos pendientes de trazar (estado=pendiente_trazar en plataforma ERP)
 *   - Remitos en tránsito (despachos del GLN sin recepción confirmada)
 *   - Recepciones pendientes (despachos hacia mi GLN, informados, no confirmados)
 *   - Errores ANMAT (operaciones del GLN con estado=error)
 *   - Series trazadas (tenencia=en_poder en mi GLN — NO es el stock físico,
 *     solo lo que pasó por SNT y está bajo mi tenencia regulatoria)
 *
 * Operaciones recientes con filtro Todas / Despachos / Recepciones / Canceladas,
 * paridad con el mockup vanilla.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck,
  AlertTriangle,
  Package,
  Activity,
  ArrowRight,
  AlertCircle,
  Loader2,
  FileWarning,
} from 'lucide-react';
import {
  useOperaciones,
  useRemitos,
  useSeries,
  useUbicaciones,
  useAnmatCola,
  useRecepcionCompletadas,
  useTrazaStats,
  useErpTasks,
} from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { fmtDateTime, fmtNum, fmtRemitoNumero } from '@/lib/format';
import { useCountUp } from '@/lib/use-count-up';
import { bucketsPorDia } from '@/lib/sparkline';
import { Sparkline } from '@/components/ui/sparkline';
import {
  DASHBOARD_KPIS_DEFAULT,
  type DashboardKpiId,
} from '@/stores/dashboard-layout';
type DashFiltro = 'todas' | 'despachos' | 'recepciones';

/**
 * Operación reciente normalizada — unifica despachos (traza_job egreso) e
 * ingresos (anmat_task recepciones completadas) en un shape común para el
 * listado del dashboard.
 */
type OpTipo = 'egreso' | 'ingreso';
type OpEstado = 'ok' | 'error' | 'encurso' | 'pendiente';
interface OpReciente {
  key: string;
  tipo: OpTipo;
  fecha: string | null;
  referencia: string;
  numeroRemito: string | null;
  estado: OpEstado;
  itemsLabel: string;
}

const OPS_ESTADO_LABEL: Record<OpEstado, string> = {
  ok: 'OK',
  error: 'Error',
  encurso: 'En curso',
  pendiente: 'Pendiente',
};
const OPS_ESTADO_BADGE: Record<OpEstado, string> = {
  ok: 'badge-green',
  error: 'badge-red',
  encurso: 'badge-blue',
  pendiente: 'badge-yellow',
};

/** Tipo de operación → cómo se muestra en operaciones recientes. */
const OPS_TIPO: Record<OpTipo, { label: string; badge: string }> = {
  egreso: { label: 'Despacho', badge: 'badge-orange' },
  ingreso: { label: 'Recepción', badge: 'badge-blue' },
};

export function DashboardPage() {
  const navigate = useNavigate();
  const usuario = useSessionStore((s) => s.usuario);
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);

  const { data: operaciones = [] } = useOperaciones();
  const { data: remitos = [] } = useRemitos();
  const { data: series = [] } = useSeries();
  const glnIdNum = ubicacionActivaId ? Number(ubicacionActivaId) : null;
  // Despachos recientes (traza_job egreso, última por referencia).
  const { data: colaData } = useAnmatCola(
    glnIdNum ? { glnId: glnIdNum, limit: 50 } : null,
  );
  // Recepciones recientes (anmat_task trazadas). El GLN va como string local.
  const { data: recepCompletadas } = useRecepcionCompletadas(ubicacionActivaId, {
    limit: 50,
  });
  const { data: ubicaciones = [] } = useUbicaciones();
  // KPI "Remitos por trazar" — count desde la tabla local `erp_task` con
  // estado='pendiente'. La actualización es manual: el operador clickea
  // "Sincronizar" en el header y se popula `erp_task` desde ACSIS. Así
  // evitamos pegarle a ACSIS en cada render del dashboard.
  // `isLoading` solo es true en el primer fetch. Sumamos `isFetching` para
  // que el spinner también aparezca durante el refetch que dispara el botón
  // "Sincronizar" del header (caso post-sync que el operador espera ver).
  const {
    data: erpTasksData,
    isLoading: isLoadingErpFirst,
    isFetching: isFetchingErp,
  } = useErpTasks(ubicacionActivaId, 'pendiente');
  const isLoadingTareas = isLoadingErpFirst || isFetchingErp;
  // KPIs "Errores ANMAT" y "Series trazadas hoy" — vienen del mismo endpoint
  // /api/traza/stats. El spinner se activa tanto en el primer load como
  // durante el refetch que dispara "Sincronizar" del header.
  const {
    data: trazaStats,
    isLoading: isLoadingStatsFirst,
    isFetching: isFetchingStats,
  } = useTrazaStats(ubicacionActivaId);
  const isLoadingStats = isLoadingStatsFirst || isFetchingStats;

  // GLN asociados al usuario (multi-GLN). El activo va resaltado en el saludo.
  const glnsAsociados = useMemo(() => {
    const ids = usuario?.ubicacionesIds ?? (usuario?.ubicacionId ? [usuario.ubicacionId] : []);
    return ids
      .map((id) => ubicaciones.find((u) => u.id === id))
      .filter((u): u is NonNullable<typeof u> => Boolean(u));
  }, [usuario, ubicaciones]);
  const [filtro, setFiltro] = useState<DashFiltro>('todas');

  // Wrapper para que onClick reciba función `void`, no `Promise<void>`
  const go = (path: string) => () => {
    void navigate(path);
  };

  // -------- KPIs (sobre el GLN activo) --------
  const kpis = useMemo(() => {
    if (!ubicacionActivaId) {
      return {
        remitosPendientes: 0,
        remitosEnTransito: 0,
        erroresCierreErp: 0,
        opsError: 0,
        seriesEnPoder: 0,
      };
    }

    // Remitos pendientes de trazar — count de `erp_task` con estado='pendiente'
    // para el GLN activo. Se actualiza al sincronizar manualmente (header).
    const remitosPendientes = erpTasksData?.tareas.length ?? 0;

    // Transferencias despachadas desde mi GLN, ya informadas, sin recepción confirmada
    const recepcionesByOpOrigen = new Set<string>();
    for (const o of operaciones) {
      if (o.tipo === 'recepcion' && o.anmat.estado !== 'cancelada' && o.opOrigenId) {
        recepcionesByOpOrigen.add(o.opOrigenId);
      }
    }
    const remitosEnTransito = operaciones.filter(
      (o) =>
        o.tipo === 'despacho_transferencia' &&
        o.ubicacionId === ubicacionActivaId &&
        (o.anmat.estado === 'informada' || o.anmat.estado === 'en_cola') &&
        !recepcionesByOpOrigen.has(o.id),
    ).length;

    // Despachos con error de cierre en el ERP — dato REAL desde el backend
    // (traza egreso success + erp_task.erp_estado_tarea_cumplida='error').
    const erroresCierreErp = trazaStats?.erroresCierreErp ?? 0;

    // Errores ANMAT del GLN activo — dato REAL desde traza_job (vía backend).
    // Cuenta la última fila por (referencia) con status error/failed; trazas
    // re-trazadas exitosamente no siguen contando.
    const opsError = trazaStats?.errores ?? 0;

    // Series trazadas hoy — dato REAL desde traza_job_detail (vía backend).
    // Cuenta items con anmat_transaction_id no null creados hoy desde el GLN.
    const seriesEnPoder = trazaStats?.seriesTrazadasHoy ?? 0;

    return { remitosPendientes, remitosEnTransito, erroresCierreErp, opsError, seriesEnPoder };
  }, [operaciones, ubicacionActivaId, trazaStats, erpTasksData]);

  // Tendencia 7d (paquete AE) — derivada de operaciones del GLN activo.
  // No es el mismo valor que el KPI principal (que es snapshot), sino el
  // VOLUMEN del último día por categoría. Da contexto temporal "¿hubo más
  // hoy que ayer?" sin tener que abrir Reportes.
  const tendencias = useMemo(() => {
    if (!ubicacionActivaId) {
      const zero = [0, 0, 0, 0, 0, 0, 0];
      return {
        remitos: zero,
        despachos: zero,
        recepciones: zero,
        errores: zero,
        series: zero,
      };
    }
    const opsDelGln = operaciones.filter((o) => o.ubicacionId === ubicacionActivaId);
    const remitosDelGln = remitos.filter((r) => r.origenUbicacionId === ubicacionActivaId);
    const seriesDelGln = series.filter((s) => s.ubicacionActualId === ubicacionActivaId);
    return {
      remitos: bucketsPorDia(remitosDelGln, (r) => r.fecha),
      despachos: bucketsPorDia(
        opsDelGln.filter((o) => o.tipo.startsWith('despacho_')),
        (o) => o.fecha,
      ),
      recepciones: bucketsPorDia(
        operaciones.filter(
          (o) => o.tipo === 'recepcion' && o.ubicacionId === ubicacionActivaId,
        ),
        (o) => o.fecha,
      ),
      errores: bucketsPorDia(
        opsDelGln.filter((o) => o.anmat.estado === 'error'),
        (o) => o.fecha,
      ),
      series: bucketsPorDia(seriesDelGln, (s) => s.createdAt),
    };
  }, [operaciones, remitos, series, ubicacionActivaId]);

  // -------- Operaciones recientes (datos REALES) --------
  // Une despachos (traza_job egreso, vía /anmat/cola) + recepciones
  // (anmat_task trazadas), normaliza a un shape común y ordena por fecha desc.
  const opsRecientes = useMemo<OpReciente[]>(() => {
    const egresos: OpReciente[] = (colaData?.items ?? []).map((o) => ({
      key: `egreso-${o.id}`,
      tipo: 'egreso',
      fecha: o.createdAt,
      referencia: o.referencia,
      numeroRemito: o.numeroRemito,
      estado:
        o.status === 'success'
          ? 'ok'
          : o.status === 'error' || o.status === 'failed'
            ? 'error'
            : 'encurso',
      itemsLabel: o.totalItems != null ? `${o.doneItems ?? 0}/${o.totalItems}` : '—',
    }));
    const ingresos: OpReciente[] = (recepCompletadas?.items ?? []).map((r) => ({
      key: `ingreso-${r.id}`,
      tipo: 'ingreso',
      fecha: r.tracedAt ?? r.createdAt,
      referencia: r.nRemito ?? r.nFactura ?? String(r.id),
      numeroRemito: r.nRemito,
      estado: r.totales.error > 0 ? 'error' : 'ok',
      itemsLabel: `${r.totales.trazado}/${r.totales.total}`,
    }));
    return [...egresos, ...ingresos].sort((a, b) => {
      const ta = a.fecha ? new Date(a.fecha).getTime() : 0;
      const tb = b.fecha ? new Date(b.fecha).getTime() : 0;
      return tb - ta;
    });
  }, [colaData, recepCompletadas]);

  const counts = useMemo(
    () => ({
      todas: opsRecientes.length,
      despachos: opsRecientes.filter((o) => o.tipo === 'egreso').length,
      recepciones: opsRecientes.filter((o) => o.tipo === 'ingreso').length,
    }),
    [opsRecientes],
  );

  const opsFiltradas = useMemo(() => {
    const list = opsRecientes.filter((o) => {
      if (filtro === 'despachos') return o.tipo === 'egreso';
      if (filtro === 'recepciones') return o.tipo === 'ingreso';
      return true;
    });
    return list.slice(0, 8);
  }, [opsRecientes, filtro]);

  return (
    <div className="space-y-6">
      {/* Banner ubicación — muestra el saludo + el GLN activo solamente.
          Los otros GLN operables (si el usuario tiene multi-GLN) se ven y
          cambian desde el LocationSwitcher del header. */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
        <span>
          Bienvenido, <b>{usuario?.nombre ?? 'invitado'}</b> — operando en:
        </span>
        {(() => {
          const activo = glnsAsociados.find((u) => u.id === ubicacionActivaId);
          if (!activo) return <span className="text-gray-500">—</span>;
          return (
            <span
              className="inline-block rounded bg-orange-50 px-2 py-0.5 text-xs font-semibold text-everex-azul ring-1 ring-everex-naranja/40"
              title="GLN activo (cambiar desde el switcher del header)"
            >
              <b>{activo.nombre}</b>{' '}
              <span className="text-[10px] text-gray-500">(GLN {activo.gln})</span>
            </span>
          );
        })()}
      </div>

      {/* Hero card — solo si hay errores ANMAT que resolver (paquete AA). */}
      {kpis.opsError > 0 ? (
        <HeroCard
          tone="red"
          icon={<AlertCircle className="h-6 w-6" />}
          titulo={`${kpis.opsError} operación${kpis.opsError === 1 ? '' : 'es'} con error en SNT`}
          subtitulo="Revisá y reintentá o editá las transacciones en estado de error antes de que se acumulen."
          ctaLabel="Resolver en Log ANMAT"
          onCta={go('/anmat?estado=error')}
        />
      ) : null}

      {/* KPIs — grilla fija en el orden default. */}
      <KpiGrid
        kpis={kpis}
        tendencias={tendencias}
        onKpiClick={(path) => go(path)()}
        isLoadingTareas={isLoadingTareas}
        isLoadingStats={isLoadingStats}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Operaciones recientes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-everex-azul">
                <Activity className="h-5 w-5" />
                Operaciones recientes
              </CardTitle>
              <div className="flex flex-wrap gap-1.5">
                <FiltroBtn val="todas" current={filtro} setter={setFiltro} count={counts.todas} label="Todas" />
                <FiltroBtn val="despachos" current={filtro} setter={setFiltro} count={counts.despachos} label="Despachos" />
                <FiltroBtn val="recepciones" current={filtro} setter={setFiltro} count={counts.recepciones} label="Recepciones" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded border border-gray-100">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead>Nº Remito</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {opsFiltradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-sm text-gray-600">
                        Sin operaciones para este filtro.
                      </TableCell>
                    </TableRow>
                  ) : (
                    opsFiltradas.map((op) => (
                      <OperacionRecienteRow key={op.key} op={op} onClick={go('/anmat')} />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="mt-2 text-[11px] text-gray-600">
              Mostrando hasta 8 operaciones recientes. Ver todas en{' '}
              <button
                className="text-everex-azul underline"
                onClick={go('/anmat')}
              >
                Log ANMAT
              </button>
              .
            </div>
          </CardContent>
        </Card>

        {/* Accesos rápidos */}
        <Card>
          <CardHeader>
            <CardTitle className="text-everex-azul">Accesos rápidos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <QuickLink
              label="Despachar remito"
              detail={
                kpis.remitosPendientes > 0
                  ? `${fmtNum(kpis.remitosPendientes)} pendiente(s) de trazar`
                  : 'Todo trazado'
              }
              tone={kpis.remitosPendientes > 0 ? 'orange' : 'green'}
              onClick={go('/despacho')}
              loading={isLoadingTareas}
              attention={!isLoadingTareas && kpis.remitosPendientes > 0}
            />
            <QuickLink
              label="Resolver errores ANMAT"
              detail={
                kpis.opsError > 0
                  ? `${fmtNum(kpis.opsError)} operación(es) con error`
                  : 'Sin errores ANMAT'
              }
              tone={kpis.opsError > 0 ? 'red' : 'green'}
              onClick={go('/anmat')}
              loading={isLoadingStats}
              attention={!isLoadingStats && kpis.opsError > 0}
            />
            <QuickLink
              label="Reintentar cierre en ERP"
              detail={
                kpis.erroresCierreErp > 0
                  ? `${fmtNum(kpis.erroresCierreErp)} despacho(s) no informado(s) al ERP`
                  : 'Todo informado al ERP'
              }
              tone={kpis.erroresCierreErp > 0 ? 'red' : 'green'}
              onClick={go('/despacho?tab=completadas&cierreErp=error')}
              loading={isLoadingStats}
              attention={!isLoadingStats && kpis.erroresCierreErp > 0}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * KpiGrid — grilla fija de los 5 KPIs del Dashboard, en el orden default.
 */
interface KpiGridProps {
  kpis: {
    remitosPendientes: number;
    remitosEnTransito: number;
    erroresCierreErp: number;
    opsError: number;
    seriesEnPoder: number;
  };
  tendencias: {
    remitos: number[];
    despachos: number[];
    recepciones: number[];
    errores: number[];
    series: number[];
  };
  onKpiClick: (path: string) => void;
  /** True mientras la query de tareas pendientes está en su primer load.
   *  Pasamos el flag para que el KPI "Remitos por trazar" muestre spinner
   *  en vez de "0" antes de tener la respuesta. */
  isLoadingTareas: boolean;
  /** Mismo motivo para el KPI "Errores ANMAT". */
  isLoadingStats: boolean;
}

function KpiGrid({
  kpis,
  tendencias,
  onKpiClick,
  isLoadingTareas,
  isLoadingStats,
}: KpiGridProps) {
  // Map id → datos para renderizar cada card.
  const kpiData: Record<DashboardKpiId, Omit<KpiCardProps, 'id'>> = {
    remitos_por_trazar: {
      label: 'Remitos por trazar',
      value: kpis.remitosPendientes,
      tone: kpis.remitosPendientes > 0 ? 'orange' : 'gray',
      subtitle: 'pendientes en ERP',
      icon: <Truck className="h-4 w-4" />,
      onClick: () => onKpiClick('/despacho'),
      tendencia: tendencias.remitos,
      tendenciaLabel: 'remitos nuevos por día',
      isLoading: isLoadingTareas,
    },
    en_transito: {
      label: 'En tránsito',
      value: kpis.remitosEnTransito,
      tone: 'blue',
      subtitle: 'despachados sin confirmar',
      icon: <ArrowRight className="h-4 w-4" />,
      onClick: () => onKpiClick('/anmat'),
      tendencia: tendencias.despachos,
      tendenciaLabel: 'despachos por día',
    },
    errores_cierre_erp: {
      label: 'Error cierre ERP',
      value: kpis.erroresCierreErp,
      tone: kpis.erroresCierreErp > 0 ? 'red' : 'gray',
      subtitle: 'despachos no informados al ERP',
      icon: <FileWarning className="h-4 w-4" />,
      onClick: () => onKpiClick('/despacho?tab=completadas&cierreErp=error'),
      isLoading: isLoadingStats,
    },
    errores_anmat: {
      label: 'Errores ANMAT',
      value: kpis.opsError,
      tone: kpis.opsError > 0 ? 'red' : 'gray',
      subtitle: 'a reintentar / editar',
      icon: <AlertTriangle className="h-4 w-4" />,
      onClick: () => onKpiClick('/despacho'),
      tendencia: tendencias.errores,
      tendenciaLabel: 'errores por día',
      isLoading: isLoadingStats,
    },
    series_en_stock: {
      label: 'Series trazadas hoy',
      value: kpis.seriesEnPoder,
      tone: 'green',
      // Items con anmat_transaction_id no null creados hoy desde el GLN
      // activo (cuenta de traza_job_detail vía /api/traza/stats).
      subtitle: 'series informadas al SNT',
      icon: <Package className="h-4 w-4" />,
      onClick: () => onKpiClick('/despacho'),
      tendencia: tendencias.series,
      tendenciaLabel: 'series nuevas por día',
      isLoading: isLoadingStats,
    },
  };

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
      {DASHBOARD_KPIS_DEFAULT.map((id) => (
        <KpiCard key={id} {...kpiData[id]} />
      ))}
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: number;
  tone: 'blue' | 'orange' | 'red' | 'purple' | 'green' | 'gray';
  subtitle?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  /** Tendencia 7d para el sparkline (paquete AE). 7 valores: día-6 → hoy. */
  tendencia?: readonly number[];
  /** Texto descriptivo de qué muestra la tendencia (para a11y). */
  tendenciaLabel?: string;
  /** Si la query que alimenta el KPI está en primer load (sin data cacheada),
   *  mostramos un spinner en vez de `0` para no confundir con "no hay nada". */
  isLoading?: boolean;
}

function KpiCard({
  label,
  value,
  tone,
  subtitle,
  icon,
  onClick,
  tendencia,
  tendenciaLabel,
  isLoading,
}: KpiCardProps) {
  const toneClass = {
    blue: 'text-blue-600',
    orange: 'text-orange-500',
    red: 'text-red-500',
    purple: 'text-purple-600',
    green: 'text-green-600',
    gray: 'text-gray-600',
  }[tone];
  // Mapeo del tone semántico al hex del brandbook para el sparkline.
  const toneHex = {
    blue: '#2563eb',
    orange: '#E05206', // naranja institucional
    red: '#dc2626',
    purple: '#9333ea',
    green: '#16a34a',
    gray: '#6b7280',
  }[tone];
  // Count-up animation: tick visual de 0 → value (paquete Y).
  // Respeta prefers-reduced-motion desde el hook.
  const displayValue = useCountUp(value);
  const sumaTendencia = tendencia ? tendencia.reduce((a, b) => a + b, 0) : 0;
  return (
    <Card
      className={cn(
        'transition',
        onClick && 'cursor-pointer hover:border-everex-naranja hover:shadow-md',
      )}
      onClick={onClick}
    >
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase text-gray-600">{label}</div>
          {icon && <div className="text-gray-600">{icon}</div>}
        </div>
        <div
          className={cn(
            'mt-1 flex h-9 items-center text-3xl font-bold tabular-nums',
            toneClass,
          )}
        >
          {isLoading ? (
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          ) : (
            fmtNum(displayValue)
          )}
        </div>
        <div className="mt-1 flex items-end justify-between gap-2">
          {subtitle && <div className="text-[11px] text-gray-600">{subtitle}</div>}
          {tendencia && sumaTendencia > 0 && (
            <div
              className="ml-auto"
              title={`Últimos 7 días — ${tendenciaLabel ?? 'tendencia'}: ${tendencia.join(', ')}`}
            >
              <Sparkline
                values={tendencia}
                color={toneHex}
                ariaLabel={tendenciaLabel ?? 'Tendencia últimos 7 días'}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Una fila de "Operaciones recientes" — despacho (egreso) o recepción (ingreso). */
function OperacionRecienteRow({
  op,
  onClick,
}: {
  op: OpReciente;
  onClick: () => void;
}) {
  return (
    <TableRow className="cursor-pointer hover:bg-gray-50" onClick={onClick}>
      <TableCell className="whitespace-nowrap text-[11px] text-gray-600">
        {op.fecha ? fmtDateTime(op.fecha) : '—'}
      </TableCell>
      <TableCell>
        <span className={cn('badge', OPS_TIPO[op.tipo].badge)}>{OPS_TIPO[op.tipo].label}</span>
      </TableCell>
      <TableCell className="font-mono text-[11px]">{op.referencia}</TableCell>
      <TableCell className="font-mono text-[11px]">
        {op.numeroRemito ? fmtRemitoNumero(op.numeroRemito) : '—'}
      </TableCell>
      <TableCell className="text-[11px]">{op.itemsLabel}</TableCell>
      <TableCell>
        <span className={cn('badge', OPS_ESTADO_BADGE[op.estado])}>
          {OPS_ESTADO_LABEL[op.estado]}
        </span>
      </TableCell>
    </TableRow>
  );
}

function FiltroBtn({
  val,
  current,
  setter,
  count,
  label,
}: {
  val: DashFiltro;
  current: DashFiltro;
  setter: (v: DashFiltro) => void;
  count: number;
  label: string;
}) {
  const active = current === val;
  return (
    <Button
      size="sm"
      variant={active ? 'default' : 'outline'}
      onClick={() => setter(val)}
      className={cn(
        'h-9 text-xs',
        active && 'bg-everex-azul text-white hover:bg-everex-azul/90',
      )}
    >
      {label}
      <span className="ml-1 text-[11px] opacity-70">({count})</span>
    </Button>
  );
}

/**
 * HeroCard — banner llamativo en el top del dashboard (paquete AA).
 * Solo se muestra si hay algo crítico que requiere acción del operador.
 * Diseñada para no aparecer cuando todo está bien (objetivo: nunca debería
 * verse en un sistema sano).
 */
function HeroCard({
  tone,
  icon,
  titulo,
  subtitulo,
  ctaLabel,
  onCta,
}: {
  tone: 'red' | 'purple' | 'amber';
  icon: React.ReactNode;
  titulo: string;
  subtitulo: string;
  ctaLabel: string;
  onCta: () => void;
}) {
  const toneClasses = {
    red: {
      bg: 'bg-gradient-to-r from-red-50 to-red-100 border-red-300',
      iconBg: 'bg-red-500',
      title: 'text-red-900',
      sub: 'text-red-800/80',
      btn: 'bg-red-600 hover:bg-red-700 text-white',
    },
    purple: {
      bg: 'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-300',
      iconBg: 'bg-purple-600',
      title: 'text-purple-900',
      sub: 'text-purple-800/80',
      btn: 'bg-purple-600 hover:bg-purple-700 text-white',
    },
    amber: {
      bg: 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300',
      iconBg: 'bg-amber-500',
      title: 'text-amber-900',
      sub: 'text-amber-800/80',
      btn: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
  }[tone];
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-center gap-4 rounded-lg border-2 p-4 shadow-everex-1',
        toneClasses.bg,
      )}
    >
      <div
        className={cn(
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white',
          toneClasses.iconBg,
        )}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className={cn('text-base font-bold', toneClasses.title)}>{titulo}</div>
        <div className={cn('text-xs', toneClasses.sub)}>{subtitulo}</div>
      </div>
      <Button onClick={onCta} className={cn('text-xs font-semibold', toneClasses.btn)}>
        {ctaLabel}
        <ArrowRight className="ml-1.5 h-3 w-3" />
      </Button>
    </div>
  );
}

function QuickLink({
  label,
  detail,
  tone,
  onClick,
  loading = false,
  attention = false,
}: {
  label: string;
  detail: string;
  tone: 'orange' | 'purple' | 'red' | 'blue' | 'green';
  onClick: () => void;
  /** Cuando true, muestra un placeholder de carga en lugar del `detail`
   *  — evita mostrar "0 pendiente(s)" antes de que llegue la respuesta. */
  loading?: boolean;
  /** Cuando true, muestra un puntito que "late" — señaliza al usuario que
   *  hay algo para hacer sin gritar. Apagado por default. */
  attention?: boolean;
}) {
  const toneClasses = {
    orange: 'bg-orange-50 hover:bg-orange-100 border-orange-200',
    purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
    red: 'bg-red-50 hover:bg-red-100 border-red-200',
    blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
    green: 'bg-green-50 hover:bg-green-100 border-green-200',
  }[tone];
  const labelTone = {
    orange: 'text-orange-800',
    purple: 'text-purple-800',
    red: 'text-red-700',
    blue: 'text-blue-800',
    green: 'text-green-800',
  }[tone];
  const dotTone = {
    orange: 'bg-orange-500',
    purple: 'bg-purple-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
  }[tone];
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative w-full rounded-lg border p-3 text-left transition',
        toneClasses,
      )}
    >
      {attention && (
        <span className="absolute right-2.5 top-2.5 flex h-2.5 w-2.5">
          <span
            className={cn(
              'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60',
              dotTone,
            )}
          />
          <span className={cn('relative inline-flex h-2.5 w-2.5 rounded-full', dotTone)} />
        </span>
      )}
      <div className={cn('text-sm font-bold', labelTone)}>{label}</div>
      {loading ? (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Cargando…</span>
        </div>
      ) : (
        <div className="mt-1 text-xs text-gray-600">{detail}</div>
      )}
    </button>
  );
}
